/**
 * Delivery Date Custom Element
 * A native JavaScript replacement for the Vue 2 + moment.js delivery date component
 */
class DeliveryDate extends HTMLElement {
	constructor() {
		super();
		this.startDate = '';
		this.endDate = '';
	}

	static get observedAttributes() {
		return ['days-from', 'days-to', 'exclusion-date'];
	}

	connectedCallback() {
		this.getDates();
		this.render();
	}

	attributeChangedCallback() {
		if (this.isConnected) {
			this.getDates();
			this.render();
		}
	}

	get daysFrom() {
		return parseInt(this.getAttribute('days-from'), 10) || 0;
	}

	get daysTo() {
		return parseInt(this.getAttribute('days-to'), 10) || 0;
	}

	get exclusionDate() {
		return this.getAttribute('exclusion-date') || '';
	}

	/**
	 * Format a date as "MMM Do" (e.g., "Jan 1st", "Feb 2nd", "Mar 3rd")
	 */
	formatDate(date) {
		const months = [
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'Jun',
			'Jul',
			'Aug',
			'Sep',
			'Oct',
			'Nov',
			'Dec',
		];
		const day = date.getDate();
		const month = months[date.getMonth()];

		// Get ordinal suffix
		const suffix = this.getOrdinalSuffix(day);

		return `${month} ${day}${suffix}`;
	}

	/**
	 * Get ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
	 */
	getOrdinalSuffix(n) {
		const s = ['th', 'st', 'nd', 'rd'];
		const v = n % 100;
		return s[(v - 20) % 10] || s[v] || s[0];
	}

	/**
	 * Format date as MM/DD/YYYY for comparison (equivalent to moment's 'L' format)
	 */
	formatDateShort(date) {
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const year = date.getFullYear();
		return `${month}/${day}/${year}`;
	}

	/**
	 * Check if a time is between two other times on the same day
	 */
	isBetween(date, startHour, startMin, endHour, endMin) {
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const timeInMinutes = hours * 60 + minutes;
		const startInMinutes = startHour * 60 + startMin;
		const endInMinutes = endHour * 60 + endMin;

		return timeInMinutes > startInMinutes && timeInMinutes < endInMinutes;
	}

	/**
	 * Add days to a date and return a new date
	 */
	addDays(date, days) {
		const result = new Date(date);
		result.setDate(result.getDate() + days);
		return result;
	}

	/**
	 * Parse exclusion date string (YYYY-MM-DD format) into a Date object
	 */
	parseSingleDate(dateStr) {
		if (!dateStr || dateStr === '') return null;

		// Parse as local date (adding time component to avoid timezone issues)
		const parts = dateStr.trim().split('-');
		if (parts.length === 3) {
			return new Date(
				parseInt(parts[0], 10),
				parseInt(parts[1], 10) - 1,
				parseInt(parts[2], 10),
				0,
				0,
				0
			);
		}
		return null;
	}

	/**
	 * Parse exclusion dates string (comma-separated YYYY-MM-DD format) into an array of Date objects
	 */
	parseExclusionDates(dateStr) {
		if (!dateStr || dateStr === '') return [];

		// Split by comma and parse each date
		const dates = dateStr
			.split(',')
			.map((d) => this.parseSingleDate(d))
			.filter((d) => d !== null);

		return dates;
	}

	getDates() {
		// Get current date and convert to PST/PDT (UTC-7)
		const date = new Date();
		const utcDate = new Date(date.toUTCString());
		utcDate.setHours(
			utcDate.getHours() - 7 + date.getTimezoneOffset() / 60
		);

		let now = new Date(utcDate);
		const exclusionDates = this.parseExclusionDates(this.exclusionDate);

		// Create a Set of exclusion date strings for faster lookup
		const exclusionSet = new Set(
			exclusionDates.map((d) => this.formatDateShort(d))
		);

		// Time limits: between 00:00:00 and 11:49:00 (exclusive on both ends due to isBetween)
		const timeLimitHour = 0;
		const timeMaxHour = 11;
		const timeMaxMin = 49;

		// Track day adjustments for daysFrom and daysTo
		let daysFromAdjust = 0;
		let daysToAdjust = 0;

		// Handle exclusion date logic at the starting point
		// This shifts the starting point if TODAY or YESTERDAY (after 10am) is excluded
		if (exclusionDates.length > 0) {
			let adjusted = false;
			let maxIterations = 10;
			let iterations = 0;

			while (!adjusted && iterations < maxIterations) {
				iterations++;
				const nowDateStr = this.formatDateShort(now);
				const isBefore10am = this.isBetween(
					now,
					timeLimitHour,
					0,
					timeMaxHour,
					timeMaxMin
				);
				let foundMatch = false;

				for (const exclusionDate of exclusionDates) {
					const exclusionDateStr =
						this.formatDateShort(exclusionDate);
					const exclusionMinusOneDay = this.addDays(
						exclusionDate,
						-1
					);
					const exclusionMinusOneDayStr =
						this.formatDateShort(exclusionMinusOneDay);

					if (nowDateStr === exclusionDateStr && isBefore10am) {
						now = this.addDays(now, 1);
						now.setHours(1, 0, 0, 0);
						foundMatch = true;
						break;
					} else if (
						nowDateStr === exclusionMinusOneDayStr &&
						!isBefore10am
					) {
						now = this.addDays(now, 2);
						now.setHours(1, 0, 0, 0);
						foundMatch = true;
						break;
					}
				}

				if (!foundMatch) {
					adjusted = true;
				}
			}
		}

		// Check if time is between midnight and 11:49am
		const isBetweenTimeLimit = this.isBetween(
			now,
			timeLimitHour,
			0,
			timeMaxHour,
			timeMaxMin
		);
		const dayOfWeek = now.getDay();

		if (dayOfWeek === 5 && !isBetweenTimeLimit) {
			// Friday after 11:49am, exclude weekend
			daysFromAdjust += 2;
			daysToAdjust += 2;
		} else if (dayOfWeek === 6) {
			// Saturday, exclude weekend
			daysFromAdjust += 1;
			daysToAdjust += 1;
		} else if (isBetweenTimeLimit) {
			// Before 11:49am, day counts
			daysFromAdjust -= 1;
			daysToAdjust -= 1;
		}

		// Calculate delivery dates, skipping over exclusion dates
		const startDateObj = this.addBusinessDays(
			now,
			this.daysFrom + daysFromAdjust,
			exclusionSet
		);
		const endDateObj = this.addBusinessDays(
			now,
			this.daysTo + daysToAdjust,
			exclusionSet
		);

		this.startDate = this.formatDate(startDateObj);
		this.endDate = this.formatDate(endDateObj);
	}

	/**
	 * Add business days to a date, skipping exclusion dates
	 */
	addBusinessDays(startDate, daysToAdd, exclusionSet) {
		let currentDate = new Date(startDate);
		let daysAdded = 0;

		while (daysAdded < daysToAdd) {
			currentDate = this.addDays(currentDate, 1);
			const dateStr = this.formatDateShort(currentDate);

			// Skip if this date is in the exclusion set
			if (!exclusionSet.has(dateStr)) {
				daysAdded++;
			}
		}

		return currentDate;
	}

	render() {
		// Find and replace template placeholders
		const template = this.querySelector('template');

		if (template) {
			// Use the template content
			const content = template.innerHTML
				.replace(/\#\{\s*startDate\s*\}/g, this.startDate)
				.replace(/\#\{\s*endDate\s*\}/g, this.endDate);

			// Create a container for the rendered content
			let container = this.querySelector('.delivery-date-content');
			if (!container) {
				container = document.createElement('div');
				container.className = 'delivery-date-content';
				this.appendChild(container);
			}
			container.innerHTML = content;
		} else {
			// Direct text node replacement for inline-template style usage
			this.innerHTML = this.innerHTML
				.replace(/\#\{\s*startDate\s*\}/g, this.startDate)
				.replace(/\#\{\s*endDate\s*\}/g, this.endDate);
		}

		// Remove v-cloak if present
		this.removeAttribute('v-cloak');
	}
}

// Register the custom element
customElements.define('delivery-date', DeliveryDate);
